// src/services/pdf/generateRecibo.js
// Generates a "Recibo de Pago" (Payment Receipt) PDF for the initial deposit (señal).

import { jsPDF } from "jspdf";
import {
    preloadLogos,
    drawHeader,
    drawFooter,
    drawDataBox,
    drawSignatureBlock,
    checkPageBreak,
} from "./pdfBranding.js";
import {
    COMPANY,
    COLORS,
    formatCurrency,
    todayFormatted,
    generateDocNumber,
    safeFileName,
    formatDate,
} from "./pdfConfig.js";

/**
 * Generate a Recibo (Receipt) PDF.
 * @param {object} event       - Datos del evento
 * @param {object} contact     - Contacto principal { name, email, phone, nif }
 * @param {object} companyData - Datos de la empresa (CIF, IBAN, etc.)
 * @param {string} docNumber   - Número de documento ya generado/incrementado
 * @param {object} receipt     - { amount, method }: señal cobrada (pagos de tipo
 *                               "advance") y método, si consta en todos ellos
 */
export async function generateReciboPDF(event, contact = {}, companyData = {}, docNumber = "", receipt = {}) {

    if (!event) return;

    // El importe es lo cobrado de verdad: un recibo sin ingreso detras no se emite.
    const amount = Number(receipt.amount) || 0;
    if (amount <= 0) throw new Error("No hay señal registrada para emitir el recibo");

    await preloadLogos();
    const doc = new jsPDF({ compress: true });
    const finalDocNumber = docNumber || generateDocNumber("R"); 
    const pageWidth = doc.internal.pageSize.getWidth();


    // === HEADER ===
    let y = drawHeader(doc, {
        title: "RECIBO DE PAGO",
        docNumber: finalDocNumber,
        subtitle: `Fecha: ${todayFormatted()}`,
    });


    // === INFO BOXES ===
    const boxWidth = (pageWidth - 45) / 2;

    // Emisor
    drawDataBox(doc, y, "Recibí de", [
        contact.name || event.client_name_legacy || "Cliente",
        contact.nif ? `NIF/CIF: ${contact.nif}` : "",
        contact.fiscal_address || "",
        `${contact.fiscal_zip || ""} ${contact.fiscal_city || ""}`.trim() || contact.email || "",
    ].filter(Boolean), { width: boxWidth, startX: 15 });


    // Receptor (Beneficiario)
    drawDataBox(doc, y, "Beneficiario", [
        companyData.name || COMPANY.name,
        `CIF: ${companyData.cif || COMPANY.cif}`,
        companyData.address || companyData.fiscal_address || COMPANY.address,
    ], { width: boxWidth, startX: 15 + boxWidth + 10 });


    y += 35;

    // === PAYMENT DETAILS ===
    doc.setFillColor(240, 255, 240);
    doc.roundedRect(15, y, pageWidth - 30, 40, 2, 2, "F");
    doc.setDrawColor(...COLORS.SUCCESS);
    doc.setLineWidth(0.5);
    doc.roundedRect(15, y, pageWidth - 30, 40, 2, 2, "S");

    doc.setTextColor(...COLORS.DARK);
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("CONCEPTO", 20, y + 8);

    doc.setTextColor(...COLORS.TEXT);
    doc.setFont(undefined, "normal");
    const concept = `Pago de señal para reserva de fecha (${formatDate(event.date)}) - ${capitalize(event.type || "Evento")}`;
    doc.text(concept, 20, y + 14);

    doc.setFont(undefined, "bold");
    doc.text("IMPORTE RECIBIDO", 20, y + 24);

    doc.setFontSize(16);
    doc.setTextColor(0, 100, 0);
    doc.text(formatCurrency(amount), 20, y + 32);

    y += 50;

    // === ADDITIONAL INFO ===
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.TEXT);
    if (receipt.method) {
        doc.text(`Método de pago: ${receipt.method}`, 15, y);
        y += 5;
    }
    doc.text(`Referencia interna: ${event.client_name_legacy || "—"}`, 15, y);

    y += 20;

    // === SIGNATURE ===
    doc.text(`Firmado en ${companyData.fiscal_city || companyData.city || "Getxo"}, a ${todayFormatted()}`, 15, y);

    y += 10;

    // Valid for "Recibí" signature only
    doc.setDrawColor(...COLORS.GRAY);
    doc.line(15, y + 25, 85, y + 25);
    doc.setFontSize(8);
    doc.text("Fdo. Los Toldos Entertainment", 15, y + 30);

    // === FOOTER ===
    drawFooter(doc);

    // === SAVE ===
    const safeName = safeFileName(event.client_name_legacy);
    doc.save(`Recibo_${safeName}_${finalDocNumber}.pdf`);

}

// ─── Helpers ───────────────────────────────────────────────────────────────
function capitalize(str) {
    return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}
